import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import { cn } from "@/lib/utils";
import { Car, Check, ChevronsUpDown } from "lucide-react";

export type LedgerProductOption = {
  id: string | number;
  name: string;
  imageUrl?: string | null;
  categoryName?: string | null;
  brandName?: string | null;
  inventoryStatus?: string | null;
  purchasePrice?: number | null;
  landedCost?: number | null;
  purchaseCurrency?: string | null;
};

type ProductSelectProps = {
  value: string;
  options: LedgerProductOption[];
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
  clearable?: boolean;
  clearLabel?: string;
};

function formatMoney(amount: number | null | undefined, currency?: string | null): string {
  if (amount == null || !Number.isFinite(Number(amount))) return "—";
  const n = Number(amount);
  if (currency && currency !== "PKR") {
    return `${currency} ${n.toLocaleString()}`;
  }
  return `Rs ${n.toLocaleString()}`;
}

function statusLabel(status: string | null | undefined): string {
  if (status === "sold") return "Sold";
  if (status === "reserved") return "Reserved";
  return "In stock";
}

function productSubtitle(opt: LedgerProductOption): string {
  return [opt.categoryName, opt.brandName].filter(Boolean).join(" · ");
}

function productPrice(opt: LedgerProductOption): number | null {
  const landed = opt.landedCost;
  if (landed != null && Number.isFinite(Number(landed))) return Number(landed);
  const purchase = opt.purchasePrice;
  if (purchase != null && Number.isFinite(Number(purchase))) return Number(purchase);
  return null;
}

export function ProductSelect({
  value,
  options,
  onChange,
  disabled = false,
  placeholder = "Select product",
  clearable = false,
  clearLabel = "All products",
}: ProductSelectProps) {
  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);

  const selectedOption = options.find((opt) => String(opt.id) === String(value));
  const displayLabel = selectedOption ? selectedOption.name : placeholder;

  return (
    <Popover open={open} onOpenChange={onOpenChange} modal>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={disabled}
          aria-expanded={open}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "glass-input h-9 w-full justify-between font-normal text-xs px-3 min-w-0",
            !selectedOption && "text-muted-foreground",
          )}
        >
          <span className="truncate">{displayLabel}</span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(24rem,calc(100vw-2rem))] p-0 z-[100]" align="start" sideOffset={4}>
        <Command
          filter={(item, search, keywords) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${item} ${(keywords ?? []).join(" ")}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput {...commandInputProps} placeholder="Search product…" className="h-9 text-xs" />
          <CommandList className="max-h-[min(24rem,70vh)] overflow-y-auto">
            <CommandEmpty className="py-2.5 text-center text-xs text-muted-foreground">
              No product found.
            </CommandEmpty>
            <CommandGroup>
              {clearable ? (
                <CommandItem
                  value={`__clear__ ${clearLabel}`}
                  onSelect={() => {
                    onChange("");
                    setOpen(false);
                  }}
                  className="text-xs flex items-center justify-between cursor-pointer text-muted-foreground"
                >
                  {clearLabel}
                  <Check className={cn("ml-2 h-3.5 w-3.5 shrink-0", !value ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ) : null}
              {options.map((opt) => {
                const isSelected = String(opt.id) === String(value);
                const subtitle = productSubtitle(opt);
                const price = productPrice(opt);
                return (
                  <CommandItem
                    key={String(opt.id)}
                    value={`product:${opt.id}`}
                    keywords={[
                      opt.name,
                      opt.categoryName ?? "",
                      opt.brandName ?? "",
                      String(opt.id),
                    ]}
                    onSelect={() => {
                      onChange(String(opt.id));
                      setOpen(false);
                    }}
                    className="cursor-pointer px-2 py-2 aria-selected:bg-accent"
                  >
                    <div className="flex w-full items-center gap-3 min-w-0">
                      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted">
                        {opt.imageUrl ? (
                          <img src={opt.imageUrl} alt="" className="h-full w-full object-cover" />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <Car className="h-6 w-6 text-muted-foreground/50" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">{opt.name}</p>
                        {subtitle ? (
                          <p className="truncate text-xs text-muted-foreground">{subtitle}</p>
                        ) : null}
                        <div className="mt-0.5 flex flex-wrap items-center gap-2">
                          <span className="text-xs font-semibold text-primary">
                            {formatMoney(price, opt.purchaseCurrency)}
                          </span>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                            {statusLabel(opt.inventoryStatus)}
                          </span>
                        </div>
                      </div>
                      <Check
                        className={cn(
                          "ml-1 h-3.5 w-3.5 shrink-0 text-primary",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
